import type { RaRecord } from "react-admin";
import { formatBookingHallNames } from "@/utils/booking-hall-names";
import { navigateToBaseRoute } from "@/utils/route-utility";

export type RentBookingReceiptRow = RaRecord & {
  receiptNo?: string;
  bookingId?: string;
  booking?: { id?: string; bookingNo?: string };
  organiserIts?: string;
  organiser?: string;
  date?: string;
  amount?: number;
  refundAmount?: number;
  mode?: string;
  type?: string;
  details?: string;
  createdBy?: string;
  admin?: { name?: string };
};

export const DEFAULT_RECEIPT_TYPE = "DEPOSIT";
export const DEFAULT_RECEIPT_MODE = "CASH";

export type ReceiptCategoryOption = {
  id: string;
  name: string;
  type?: string;
  mode?: string;
};

export const ALL_RECEIPT_CATEGORY: ReceiptCategoryOption = { id: "ALL", name: "All" };

export const CONTRIBUTIONS_RECEIPT_CATEGORY: ReceiptCategoryOption = {
  id: "RENT",
  name: "CONTRIBUTIONS - ALL",
  type: "RENT",
};

export const RECEIPT_CATEGORY_OPTIONS: ReceiptCategoryOption[] = [
  ALL_RECEIPT_CATEGORY,
  { id: "DEPOSIT|CASH", name: "DEPOSIT - CASH", type: "DEPOSIT", mode: "CASH" },
  CONTRIBUTIONS_RECEIPT_CATEGORY,
  { id: "RENT|CASH", name: "CONTRIBUTIONS - CASH", type: "RENT", mode: "CASH" },
  { id: "RENT|ONLINE", name: "CONTRIBUTIONS - ONLINE", type: "RENT", mode: "ONLINE" },
  { id: "RENT|CHEQUE", name: "CONTRIBUTIONS - CHEQUE", type: "RENT", mode: "CHEQUE" },
];

export const receiptCategoryId = (type?: string, mode?: string) => {
  if (!type && !mode) return ALL_RECEIPT_CATEGORY.id;
  const exact = RECEIPT_CATEGORY_OPTIONS.find(
    (option) => option.type === type && option.mode === mode
  );
  if (exact) return exact.id;
  if (type === "RENT" && !mode) return CONTRIBUTIONS_RECEIPT_CATEGORY.id;
  return ALL_RECEIPT_CATEGORY.id;
};

export const receiptTypeLabel = (type?: string) => {
  if (type === "RENT") return "CONTRIBUTIONS";
  if (type === "DEPOSIT") return "DEPOSIT";
  return type ?? "—";
};

export const receiptCategoryLabel = (record: RentBookingReceiptRow) => {
  const { type, mode } = record;
  const match = RECEIPT_CATEGORY_OPTIONS.find(
    (option) => option.type === type && option.mode === mode
  );
  if (match && match.id !== ALL_RECEIPT_CATEGORY.id) return match.name;
  if (!type && !mode) return "—";
  if (type && mode) return `${receiptTypeLabel(type)} - ${mode}`;
  return receiptTypeLabel(type);
};

export const bookingNo = (record: RentBookingReceiptRow) => {
  const booking = record.booking as { bookingNo?: string } | undefined;
  return booking?.bookingNo || record.bookingId || "—";
};

export const bookingId = (record: RentBookingReceiptRow) => {
  const booking = record.booking as { id?: string } | undefined;
  return booking?.id || record.bookingId;
};

export const navigateToBookingShow = (id: string | number) => {
  navigateToBaseRoute("bookings", null, ["bookings", String(id), "show"]);
};

export const createdByLabel = (record: RentBookingReceiptRow) =>
  record.admin?.name || record.createdBy || "—";

export const openReceiptPrint = (id: string | number) => {
  window.open(`#/cont-rcpt/${id}`, "_blank");
};

export const getFilterFromURL = () => {
  const fallback = { type: DEFAULT_RECEIPT_TYPE, mode: DEFAULT_RECEIPT_MODE };
  if (typeof window === "undefined") return fallback;

  const filterParam = new URLSearchParams(window.location.search).get("filter");
  if (!filterParam) return fallback;

  try {
    const parsed = JSON.parse(filterParam) as Record<string, string>;
    if (!parsed.type && !parsed.mode) {
      return parsed;
    }
    if (parsed.type && !parsed.mode) {
      return parsed;
    }
    return {
      ...parsed,
      type: parsed.type || DEFAULT_RECEIPT_TYPE,
      mode: parsed.mode || DEFAULT_RECEIPT_MODE,
    };
  } catch {
    return fallback;
  }
};

export const rentExportFilenameSuffix = (type?: string, mode?: string) => {
  if (!type && !mode) return "all";
  if (type === "RENT" && !mode) return "contributions";
  const tab = RECEIPT_CATEGORY_OPTIONS.find(
    (option) => option.type === type && option.mode === mode
  );
  if (tab?.name) {
    return tab.name.toLowerCase().replace(/\s+/g, "").replace(/-/g, "");
  }
  if (type && mode) {
    let typePart = type.toLowerCase();
    if (typePart === "rent") typePart = "contributions";
    return `${typePart}${mode.toLowerCase()}`;
  }
  return "receipts";
};

export const hallNamesLabel = (record: RaRecord) => formatBookingHallNames(record) || "—";
